import React from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const DEFAULT_FAQS = [
  {
    q: "Who do you typically work with?",
    a: "Founder-led and scale-up organisations in Bangalore across IT services, Product, AI and Real Estate. We also partner with people leaders (CHROs, VP People) at 60–600 headcount companies navigating growth, funding or cultural inflection points.",
  },
  {
    q: "Are you an executive search firm?",
    a: "No. We are an HR advisory practice. We solve the upstream problems — strategy, culture, performance, rewards, org design — that make hiring and retention work. We can help you design your hiring engine, but we do not run retainer-based executive search.",
  },
  {
    q: "What does a typical engagement look like?",
    a: "Every engagement begins with a Diagnose sprint — a 2 to 4 week deep listening exercise across leadership, employees and data. We then Discover sharp priorities and Deliver a modular advisory scope — usually 8 to 16 weeks, with embedded support thereafter if needed.",
  },
  {
    q: "Do you work fractionally or full-time?",
    a: "Fractional and project-based. Many of our clients bring us in for 2–4 days a week over a defined quarter. We do not replace an internal People team — we upgrade it.",
  },
  {
    q: "How do you price your work?",
    a: "Modular and transparent. Diagnose is a fixed-fee sprint. Discover and Deliver are scoped based on the modules selected and the organisation's maturity. We prefer outcome-linked pricing where the metric is clean.",
  },
  {
    q: "Do you work outside Bangalore?",
    a: "Our practice is rooted in Bangalore talent dynamics, but we partner with Bangalore-headquartered companies with distributed teams across India, the US and EMEA.",
  },
];

export default function FAQSection({ faqs = DEFAULT_FAQS, eyebrow = "Questions" }) {
  return (
    <section id="faq" data-testid="faq-section" className="bg-[#070709] border-t border-ink-600">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-10 mb-10">
          <div className="col-span-12 md:col-span-5">
            <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-4">
              {eyebrow}
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight">
              Frequently asked,
              <span className="block text-zinc-500 italic font-medium">honestly answered.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-sm md:text-base text-zinc-400">
              Didn&apos;t find what you were looking for? Drop us a note on the{" "}
              <Link
                to="/contact"
                className="text-cy hover:text-white underline-offset-4 hover:underline"
              >
                contact page
              </Link>{" "}
              — we reply to every inquiry personally.
            </p>
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full border-t border-ink-500">
          {faqs.map((item, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border-b border-ink-500"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="py-5 text-left font-display text-base md:text-lg font-semibold tracking-tight text-white hover:no-underline hover:text-cy data-[state=open]:text-cy">
                <span className="flex items-center gap-5 text-left">
                  <span className="font-mono text-xs text-zinc-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {item.q}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm md:text-base text-zinc-400 leading-relaxed pl-10 max-w-3xl">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
